import { isbot } from 'isbot';

/**
 * User-agent and browser-environment bot signals.
 *
 * Static analysis only: user agent string, browser feature surface, and
 * referrer. Does not require user interaction tracking.
 *
 * @module bot-detection/ua-signals
 */

/** `isBot` is derived from `confidence` alone (>= 0.5); the flags are advisory. */
export interface BotDetectionResult {
  isBot: boolean;
  botType?: BotType;
  confidence: number;
  reasons: string[];
  userAgent?: string;
  behaviorFlags: BotBehaviorFlags;
}

export type BotType = 'search_engine' | 'crawler' | 'automated' | 'suspicious';

/**
 * Advisory flags. Only `invalidReferrer` is populated here; `noInteraction` and
 * `tooFast` are filled in later by `performComprehensiveBotAnalysis`, and
 * `suspiciousPattern` is currently never set by anything (placeholder).
 */
export interface BotBehaviorFlags {
  noInteraction: boolean;
  tooFast: boolean;
  suspiciousPattern: boolean;
  invalidReferrer: boolean;
}

// Loopback referrers mean the page was opened from a local harness, not a real
// inbound link — the usual shape of a scripted/headless visit.
const SUSPICIOUS_REFERRER_PATTERNS = [
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
] as const;

/**
 * Static analysis of user agent, browser feature surface, and referrer.
 *
 * Confidence is additive and clamped to 1; the thresholds below were tuned to
 * avoid flagging privacy-hardened real browsers, not derived from a model.
 */
export function analyzeUserAgentForBotSignals(): BotDetectionResult {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const reasons: string[] = [];
  let botType: BotType | undefined;
  let confidence = 0;

  if (userAgent) {
    if (isbot(userAgent)) {
      reasons.push(`User agent contains bot pattern`);
      confidence += 0.9;
      const lowerUserAgent = userAgent.toLowerCase();
      if (
        lowerUserAgent.includes('googlebot') ||
        lowerUserAgent.includes('bingbot')
      ) {
        botType = 'search_engine';
      } else if (
        lowerUserAgent.includes('crawler') ||
        lowerUserAgent.includes('spider')
      ) {
        botType = 'crawler';
      } else {
        botType = 'automated';
      }
    }
  } else {
    // No real browser omits the UA header; an empty one implies a hand-rolled
    // client (or SSR, which is why this only runs client-side).
    reasons.push('Missing user agent');
    confidence += 0.9;
    botType = 'suspicious';
  }

  if (typeof window !== 'undefined') {
    const browserFeatures = {
      localStorage: typeof Storage !== 'undefined',
      sessionStorage: typeof sessionStorage !== 'undefined',
      indexedDB: 'indexedDB' in window,
      webdriver: 'webdriver' in navigator,
      languages: navigator.languages?.length > 0,
      onLine: 'onLine' in navigator,
      serviceWorker: 'serviceWorker' in navigator,
      geolocation: 'geolocation' in navigator,
    };

    const availableFeatureCount =
      Object.values(browserFeatures).filter(Boolean).length;

    // navigator.webdriver is not proof of automation: devtools protocol
    // sessions and some extensions set it on ordinary browsers. Only a near-
    // empty feature surface alongside it is treated as a strong signal.
    if (browserFeatures.webdriver) {
      const hasFullBrowserFeatures = availableFeatureCount >= 7;
      const hasLegitimateUserAgent =
        userAgent &&
        (userAgent.includes('Chrome/') ||
          userAgent.includes('Firefox/') ||
          userAgent.includes('Safari/') ||
          userAgent.includes('Edge/'));

      if (hasFullBrowserFeatures && hasLegitimateUserAgent) {
        reasons.push(
          'WebDriver detected but browser appears legitimate (extensions/devtools may cause this)',
        );
        confidence += 0.3;
      } else {
        reasons.push('WebDriver detected (often used by automation tools)');
        confidence += 0.8;
        if (!botType) botType = 'automated';
      }
    }

    // Deliberately lenient: privacy-focused users disable enough of these that
    // a mid-range count says nothing. Only a near-empty surface, or a thin one
    // paired with webdriver, contributes.
    if (availableFeatureCount < 3) {
      reasons.push(
        `Very limited browser features detected (${availableFeatureCount}/8)`,
      );
      confidence += 0.5;
    } else if (availableFeatureCount < 5 && browserFeatures.webdriver) {
      reasons.push(
        `Limited browser features with WebDriver (${availableFeatureCount}/8)`,
      );
      confidence += 0.3;
    }
  }

  let invalidReferrer = false;
  if (typeof document !== 'undefined' && document.referrer) {
    const referrer = document.referrer.toLowerCase();
    if (
      SUSPICIOUS_REFERRER_PATTERNS.some((pattern) => referrer.includes(pattern))
    ) {
      invalidReferrer = true;
      reasons.push('Suspicious referrer detected');
      confidence += 0.2;
    }
  }

  confidence = Math.min(confidence, 1);

  const isBot = confidence >= 0.5;

  const behaviorFlags: BotBehaviorFlags = {
    // Overwritten by performComprehensiveBotAnalysis once the interaction
    // tracker has data; false here means "not yet measured", not "human".
    noInteraction: false,
    tooFast: false,
    // No navigation-pattern analysis exists yet, so this stays false.
    suspiciousPattern: false,
    invalidReferrer,
  };

  return {
    isBot,
    botType,
    confidence,
    reasons,
    userAgent,
    behaviorFlags,
  };
}
